import api from "apis";
import { PaginatedResponse, PaginationParams } from "apis/types/general";
import getSerializedQueryArgs from "utils/getSerializedQueryArgs";

export const PREVIEW_USERS_PAGE_SIZE = 20;

export interface PreviewUsersInfiniteParams {
  page: number;
  search?: string;
  is_superuser?: boolean;
  job_classification?: string;
  department?: string;
  zone?: string;
  location?: string;
}

export interface CreateRoleRequest {
  name: string;
  permissions: number[];
}

export const permissionsApi = api.injectEndpoints({
  endpoints: (build) => ({
    getPermissions: build.query<any, PaginationParams>({
      query: (params) => ({
        url: "/permissions/",
        params,
      }),
      providesTags: ["permissions"],
      serializeQueryArgs: getSerializedQueryArgs,
    }),
    getRoles: build.query<any, PaginationParams>({
      query: (params) => ({
        url: "/groups/",
        params,
      }),
      providesTags: ["permissions"],
      serializeQueryArgs: getSerializedQueryArgs,
    }),
    createRole: build.mutation<any, CreateRoleRequest>({
      query: (body) => ({
        url: "/groups/",
        method: "POST",
        body,
      }),
      invalidatesTags: ["permissions"],
    }),
    deleteRole: build.mutation<any, any>({
      query: (id) => ({
        url: `/groups/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: ["permissions"],
    }),
    updateRolePermissions: build.mutation<any, any>({
      query: (body) => ({
        url: `/groups/${body.id}/`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["permissions"],
    }),
    updateRoleUsers: build.mutation<any, any>({
      query: (body) => ({
        url: `/groups/${body.id}/edit_staff_roles/`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["permissions", "accessList"],
    }),
    updateSuperUsers: build.mutation<any, any>({
      query: (body) => ({
        url: `/users/bulkupdatesuperusers/bulk_update_superusers/`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["permissions", "accessList"],
    }),
    getAccessList: build.query<any, any>({
      query: (params) => ({
        url: `/auth/staff/access-list/`,
        params,
      }),
      providesTags: ["accessList"],
    }),
    postAccessList: build.mutation<any, any>({
      query: (body) => ({
        url: `/auth/staff/access-list/`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["accessList", "users"],
    }),
    updateAccessList: build.mutation<any, any>({
      query: (body) => ({
        url: `/auth/staff/access-list/${body.id}/`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["accessList"],
    }),
    // Infinite list: every page for the same filters is appended into one cache entry
    getPreviewUsersInfinite: build.query<
      PaginatedResponse<any> & { page: number },
      PreviewUsersInfiniteParams
    >({
      query: (params) => ({
        url: `/staff/all/preview_users/`,
        params: { ...params, page_size: PREVIEW_USERS_PAGE_SIZE },
      }),
      transformResponse: (response: PaginatedResponse<any>, _meta, arg) => ({
        ...response,
        page: arg.page,
      }),
      providesTags: ["permissions"],
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        const { page, ...filters } = queryArgs;
        return getSerializedQueryArgs({ endpointName, queryArgs: filters });
      },
      forceRefetch({ currentArg, previousArg }) {
        return currentArg?.page !== previousArg?.page;
      },
      merge: (currentCache, newItems) => {
        if (newItems.page === 1) return newItems;
        const loadedIds = new Set(currentCache.results.map((u: any) => u.id));
        currentCache.results.push(
          ...newItems.results.filter((u: any) => !loadedIds.has(u.id)),
        );
        currentCache.next = newItems.next;
        currentCache.count = newItems.count;
        currentCache.page = newItems.page;
        return undefined;
      },
    }),
    // Walks every page so callers get the complete list (super users are few)
    getAllPreviewUsers: build.query<any[], any>({
      async queryFn(params, _api, _extraOptions, baseQuery) {
        const users: any[] = [];
        let page = 1;
        // eslint-disable-next-line no-constant-condition
        while (true) {
          // eslint-disable-next-line no-await-in-loop
          const response = await baseQuery({
            url: `/staff/all/preview_users/`,
            params: { ...params, page, page_size: PREVIEW_USERS_PAGE_SIZE },
          });
          if (response.error) return { error: response.error };
          const data = response.data as PaginatedResponse<any>;
          users.push(...(data?.results ?? []));
          if (!data?.next) return { data: users };
          page += 1;
        }
      },
      providesTags: ["permissions"],
    }),
    addPermission: build.mutation<any, PaginationParams>({
      query: (body) => ({
        url: "/permissions/",
        method: "POST",
        body,
      }),
      invalidatesTags: ["permissions"],
    }),
  }),
});

export const {
  useGetPermissionsQuery,
  useGetRolesQuery,
  useCreateRoleMutation,
  useGetPreviewUsersInfiniteQuery,
  useGetAllPreviewUsersQuery,
  useDeleteRoleMutation,
  useGetAccessListQuery,
  useUpdateRolePermissionsMutation,
  useUpdateRoleUsersMutation,
  useUpdateAccessListMutation,
  usePostAccessListMutation,
  useUpdateSuperUsersMutation,
} = permissionsApi;
